// React Query hooks for auth. Source of truth is the cookie-backed /api/me;
// the Zustand store mirrors the resolved user for components that don't want
// to subscribe to the query directly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CompanionId, PublicUser, QuickSignupResponse } from '@skena/schema'
import { AuthError, authApi } from './api.ts'
import { useAuthStore } from './store.ts'

const ME_QUERY_KEY = ['me'] as const

export function useMe() {
  const setUser = useAuthStore((s) => s.setUser)
  return useQuery<PublicUser | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const user = await authApi.me()
        setUser(user)
        return user
      } catch (err) {
        if (err instanceof AuthError && err.status === 401) {
          setUser(null)
          return null
        }
        throw err
      }
    },
    // /api/me result is cookie-derived; no point polling — invalidate on mutation.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
}

export function useLoginByToken() {
  const qc = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)
  return useMutation<PublicUser, AuthError, string>({
    mutationFn: (token) => authApi.byToken(token),
    onSuccess: (user) => {
      setUser(user)
      qc.setQueryData(ME_QUERY_KEY, user)
    },
  })
}

export function useLoginByCode() {
  const qc = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)
  return useMutation<PublicUser, AuthError, { classCode: string; displayName: string }>({
    mutationFn: ({ classCode, displayName }) => authApi.login(classCode, displayName),
    onSuccess: (user) => {
      setUser(user)
      qc.setQueryData(ME_QUERY_KEY, user)
    },
  })
}

export function useQuickSignup() {
  const qc = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)
  return useMutation<QuickSignupResponse, AuthError, string>({
    mutationFn: (displayName) => authApi.quickSignup(displayName),
    onSuccess: ({ user }) => {
      setUser(user)
      qc.setQueryData(ME_QUERY_KEY, user)
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)
  return useMutation<{ ok: true }, AuthError, void>({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      setUser(null)
      qc.setQueryData(ME_QUERY_KEY, null)
    },
  })
}

/**
 * Set the remembered grace companion (practice-loop-v2). Optimistic: the
 * ['me'] cache flips immediately so a mid-round switch takes effect on the
 * spot; on PATCH failure we keep the local choice for this session (the
 * practice UI shows a gentle "下次連線再幫你記住" toast) — the cache is NOT
 * rolled back, matching spec "a failed PATCH keeps the local choice".
 */
export function useSetCompanion() {
  const qc = useQueryClient()
  const setUser = useAuthStore((s) => s.setUser)
  return useMutation<PublicUser, AuthError, CompanionId>({
    mutationFn: (companionId) => authApi.setCompanion(companionId),
    onMutate: async (companionId) => {
      await qc.cancelQueries({ queryKey: ME_QUERY_KEY })
      qc.setQueryData<PublicUser | null>(ME_QUERY_KEY, (prev) =>
        prev ? { ...prev, companionId } : prev,
      )
    },
    onSuccess: (user) => {
      qc.setQueryData(ME_QUERY_KEY, user)
      setUser(user)
    },
  })
}
